import { runCreateWorkspaceWorkflow } from "@/action-dialog";
import { useCreateCanvas } from "@/api/canvas";
import { useCreateWorkspace, usePrivateWorkspace } from "@/api/workspaces";
import { ROUTES } from "@/constants/routes";
import { useCreatePrivateWorkspaceChat } from "@/pages/workspaces/hooks/useChatCardActions";
import { route } from "@/utils/route";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

/**
 * The "+ New" verbs, shared by the browser header's chip and the page's
 * right-click menu so the two can never offer different things. Home scope
 * (no workspaceId) includes New Workspace; a scoped workspace page omits it —
 * no nesting.
 */
export function useHomeCreateActions(workspaceId?: string) {
  const navigate = useNavigate();
  const privateWorkspace = usePrivateWorkspace();
  const targetWorkspaceId = workspaceId ?? privateWorkspace?.id;

  const createCanvas = useCreateCanvas({
    onSuccess: (c) => navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: c.id })),
    onError: () => toast.error("Failed to create canvas"),
  });
  const { create: createPrivateChat, isPending: isChatPending } =
    useCreatePrivateWorkspaceChat();
  const createWorkspace = useCreateWorkspace();
  const openCreateWorkspace = useCallback(() => {
    void runCreateWorkspaceWorkflow({
      createWorkspace,
      // Home no longer scopes to a workspace (no rail), so open the new one.
      onSuccess: (ws) =>
        navigate(route(ROUTES.WORKSPACES.DETAIL, { workspaceId: ws.id })),
    });
  }, [createWorkspace, navigate]);

  return {
    onNewCanvas: () => createCanvas.mutate(workspaceId ? { workspaceId } : {}),
    onNewChat: () => {
      if (targetWorkspaceId) createPrivateChat(targetWorkspaceId);
    },
    newChatDisabled: !targetWorkspaceId,
    onNewWorkspace: workspaceId ? undefined : openCreateWorkspace,
    isCreating: createCanvas.isPending || isChatPending,
  };
}
